import { fabric } from "fabric";
import { useEffect, useMemo, useState } from "react";

import {
  FontWeight,
  normalizeWeight,
  resolveFont,
} from "../../fonts/font-registry";
import { hasTextAttrs } from "../state/attributes";
import { DocumentDesignState } from "../state/types";

type UsedFont = [family: string, weight: FontWeight];

/**
 * The distinct (family, weight) pairs a document actually uses — the unit of
 * lazy loading. Only text-bearing items carry font attributes.
 */
export function usedFonts(state: DocumentDesignState): UsedFont[] {
  const uniquePairs = new Map<string, UsedFont>();

  for (const item of state.items) {
    if (!hasTextAttrs(item) || !item.fontFamily) {
      continue;
    }
    const weight = normalizeWeight(item.fontWeight);
    uniquePairs.set(`${item.fontFamily}__${weight}`, [item.fontFamily, weight]);
  }

  return [...uniquePairs.values()];
}

const FONT_WEIGHT_VALUE: Record<FontWeight, string> = {
  normal: "400",
  bold: "700",
};

const loadedFonts = new Map<string, Promise<void>>();

/**
 * Loads one bundled font into the browser as a `FontFace`, registered under the
 * requested weight (so Canvas2D never synthesizes a weight the PDF lacks). The
 * same bundled file the PDF embeds is used here, so preview and PDF match.
 * Loaded at most once per session; a failed load is evicted so it can retry.
 */
export function loadFont(
  family: string,
  rawWeight: string | number,
): Promise<void> {
  const weight = normalizeWeight(rawWeight);
  const cacheKey = `${family}__${weight}`;

  const cachedLoad = loadedFonts.get(cacheKey);
  if (cachedLoad) {
    return cachedLoad;
  }

  const loading = (async (): Promise<void> => {
    const base64Content = await resolveFont(family, weight).load();
    const fontBuffer = Uint8Array.from(atob(base64Content), (character) =>
      character.charCodeAt(0),
    ).buffer;
    const fontFace = new FontFace(family, fontBuffer, {
      weight: FONT_WEIGHT_VALUE[weight],
    });
    document.fonts.add(fontFace);
    await fontFace.load();
  })().catch((error: unknown) => {
    loadedFonts.delete(cacheKey);
    throw error;
  });

  loadedFonts.set(cacheKey, loading);
  return loading;
}

/**
 * Loads every font a document uses. Resilient by design: one failing font never
 * rejects the batch, so a single bad font can't block the rest of the document.
 */
export async function loadDocumentFonts(
  state: DocumentDesignState,
): Promise<void> {
  await Promise.allSettled(
    usedFonts(state).map(([family, weight]) => loadFont(family, weight)),
  );
}

interface FabricFontCacheUtil {
  clearFabricFontCache: (fontFamily?: string) => void;
}

/**
 * Drops fabric's per-family text-measurement cache (`fabric.charWidthsCache`).
 * Required after loading fonts: the first render measures with the fallback
 * font and caches those widths by family name, so without clearing, a re-render
 * reuses stale fallback metrics and the layout stays wrong.
 */
export function clearFabricFontCache(): void {
  (fabric.util as unknown as FabricFontCacheUtil).clearFabricFontCache();
}

/**
 * Loads a document's fonts and signals when they are ready so the canvas can be
 * re-rendered with real metrics. Keyed on the used-font *set*, so it only runs
 * when that set changes (opening a design, or picking a new font). Returns a
 * version counter to thread into the canvas render effect's dependencies.
 */
export function useDocumentFonts(state: DocumentDesignState): number {
  const fontsKey = useMemo(
    () =>
      usedFonts(state)
        .map(([family, weight]) => `${family}__${weight}`)
        .sort()
        .join("|"),
    [state],
  );
  const [fontsVersion, setFontsVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fontPairs = fontsKey
      ? fontsKey
          .split("|")
          .map((entry) => entry.split("__") as [string, FontWeight])
      : [];

    void Promise.allSettled(
      fontPairs.map(([family, weight]) => loadFont(family, weight)),
    ).then(() => {
      if (cancelled) {
        return;
      }
      clearFabricFontCache();
      setFontsVersion((version) => version + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [fontsKey]);

  return fontsVersion;
}
